import { useMemo } from "react";
import { Modal, Text, Button, VStack, Icon } from "@repo/ui";
import { useTheme } from "@repo/tokens";
import { useTranslation } from "react-i18next";
import { useIsMobile } from "@repo/utils";
import styles from "./FlashcardLeaveModal.module.scss";

type FlashcardLeaveModalProps = {
  isOpen: boolean;
  onResume: () => void;
  onQuit: () => void;
  subject?: string;
  title?: string | null;
  description?: string | null;
  leftButtonText?: string | null;
  rightButtonText?: string | null;
};

export function FlashcardLeaveModal({
  isOpen,
  onResume,
  onQuit,
  subject,
  title,
  description,
  leftButtonText,
  rightButtonText,
}: FlashcardLeaveModalProps) {
  const { theme } = useTheme();
  const { t } = useTranslation("selflearn");
  /** Bottom sheet only on phone; tablet and desktop use a centered dialog. */
  const isBottomSheet = useIsMobile(768);

  const modalChromeStyles = useMemo(() => {
    const bodyBase = {
      overflowY: "auto" as const,
      minHeight: 0,
    };

    if (isBottomSheet) {
      return {
        overlay: {
          padding: 0,
        },
        modal: {
          maxHeight: "min(92dvh, 92vh)",
          borderBottomLeftRadius: 0,
          borderBottomRightRadius: 0,
          borderTopLeftRadius: 40,
          borderTopRightRadius: 40,
        },
        body: bodyBase,
      };
    }

    return {
      overlay: {
        padding: "var(--spacing-lg)",
        boxSizing: "border-box" as const,
      },
      modal: {
        maxHeight: "min(90dvh, 90vh)",
        borderTopLeftRadius: 24,
        borderTopRightRadius: 24,
        borderBottomLeftRadius: 24,
        borderBottomRightRadius: 24,
        boxShadow: "0 8px 32px rgba(0, 0, 0, 0.18)",
      },
      body: bodyBase,
    };
  }, [isBottomSheet, theme]);

  return (
    <Modal
      isModalOpen={isOpen}
      onClose={onResume}
      showHeader={false}
      showFooter={false}
      justifyContent={isBottomSheet ? "flex-end" : "center"}
      alignItems={isBottomSheet ? "flex-start" : "center"}
      width={isBottomSheet ? "100%" : "min(100%, 37rem)"}
      height="auto"
      modalClassName={isBottomSheet ? styles.modalSheet : styles.modalCenter}
      styles={modalChromeStyles}
      body={
        <div
          className={
            isBottomSheet
              ? `${styles.box} ${styles.boxSheet}`
              : `${styles.box} ${styles.boxCentered}`
          }
        >
          <div className={styles.closeRow}>
            <button
              type="button"
              className={styles.closeBtn}
              onClick={onResume}
              aria-label={t("leaveModal.resume")}
            >
              <Icon
                name="closeIcon"
                width={24}
                height={24}
                color={theme.colors.primary[40]}
              />
            </button>
          </div>
          <div className={styles.iconWrap}>
            <Icon name="selflearnConfirmationIcon" width={151} height={170} />
          </div>
          <VStack className={styles.textBlock} align="center">
            {title !== null && (
              <Text className={styles.subheading} align="center">
                {title ?? t("leaveModal.title")}
              </Text>
            )}
            {subject ? (
              <Text align="center">
                {t("leaveModal.subjectExercise", { subject })}
              </Text>
            ) : null}
            {description !== null && (
              <Text className={styles.heading} align="center">
                {description ?? t("leaveModal.confirmation")}
              </Text>
            )}
          </VStack>
          <div className={styles.footer}>
            <div className={styles.footerHalf}>
              {leftButtonText !== null && (
                <Button
                  width="100%"
                  height={48}
                  variant="ghost"
                  size="md"
                  onPress={onQuit}
                  textColor={theme.colors.primary[40]}
                >
                  {leftButtonText ?? t("leaveModal.quit")}
                </Button>
              )}
            </div>
            <div className={styles.footerHalf}>
              {rightButtonText !== null && (
                <Button
                  width="100%"
                  height={48}
                  variant="primary"
                  size="md"
                  onPress={onResume}
                  shape="circle"
                  backgroundColor={theme.colors.primary[30]}
                  textColor={theme.colors.neutral[100]}
                >
                  {rightButtonText ?? t("leaveModal.resume")}
                </Button>
              )}
            </div>
          </div>
        </div>
      }
    />
  );
}
